"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DueDateFieldLogic = void 0;
const date_fns_1 = require("date-fns");
const fields_logic_registry_1 = require("../../field-type-registry/fields-logic-registry");
const types_1 = require("@smartsuite/types");
const sort_1 = require("../../sort");
const utils_1 = require("../../spotlight/utils");
const dom_1 = require("../../utils/dom");
const due_date_1 = require("../../utils/due-date");
const texts_1 = require("../../texts");
const field_logic_1 = require("../../field-type-registry/field-logic");
const intl_1 = require("../../locale/intl");
const due_date_2 = require("../../aggregations/due-date");
const date_1 = require("../../aggregations/date");
const due_date_comparisons_1 = require("./due-date.comparisons");
const due_date_fieldtype_1 = require("../../safe-field-value/date-fields/due-date-field/due-date.fieldtype");
const clipboard_1 = require("../../clipboard");
const validation_1 = require("../../validation");
const fields_1 = require("../../utils/fields");
const date_2 = require("../../utils/date");
const date_range_1 = require("../../utils/date-range");
const date_range_2 = require("../../formatters/date-range");
const utils_2 = require("../../clipboard/utils");
const migration_1 = require("../../utils/migration");
const date_3 = require("../../filter/date");
const react_fast_compare_1 = __importDefault(require("react-fast-compare"));
const clipboard_2 = require("../../clipboard/clipboard");
const google_sheet_1 = require("../../utils/google-sheet");
class DueDateFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.aggregates = {
            empty: { label: (0, intl_1.$t)(texts_1.aggregatesText.empty), aggregator: field_logic_1.countEmpty },
            "non-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.non_empty),
                aggregator: field_logic_1.countFilled,
            },
            "percent-filled": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_filled),
                aggregator: field_logic_1.percentFilled,
            },
            "percent-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_empty),
                aggregator: field_logic_1.percentEmpty,
            },
            "earliest-date": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.earliest_date),
                aggregator: (0, due_date_2.dueDateAggregate)(date_1.minDate),
            },
            "latest-date": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.latest_date),
                aggregator: (0, due_date_2.dueDateAggregate)(date_1.maxDate),
            },
            "date-range": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.date_range),
                aggregator: (0, due_date_2.dueDateAggregate)((dates, ...props) => {
                    if (!dates.length)
                        return null;
                    return `${(0, date_1.minDate)(dates, ...props)} - ${(0, date_1.maxDate)(dates, ...props)}`;
                }),
            },
        };
        this.isDate = true;
        this.isDateRange = true;
        this.name = "duedatefield";
        this.label = (0, intl_1.$t)(texts_1.dueDateFieldText.due_date);
        this.format = "YYYY-MM-DD";
        this.icon = "due-date";
        this.iconColor = "#883CD0";
        this.description = (0, intl_1.$t)(texts_1.dueDateFieldText.due_date_description);
        this.placeholder = this.format;
        this.uneditableFieldError = (0, intl_1.$t)(texts_1.dueDateFieldText.status_error);
        this.lookupMode = "lookup";
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is: due_date_comparisons_1.isDueDate,
            is_not: due_date_comparisons_1.isNotDueDate,
            is_empty: due_date_comparisons_1.isDueDateEmpty,
            is_not_empty: due_date_comparisons_1.isNotDueDateEmpty,
            is_before: due_date_comparisons_1.isDueDateBefore,
            is_after: due_date_comparisons_1.isDueDateAfter,
            is_on_or_before: due_date_comparisons_1.isDueDateOnOrBefore,
            is_on_or_after: due_date_comparisons_1.isDueDateOnOrAfter,
            is_overdue: due_date_comparisons_1.isDueDateOverdue,
            is_not_overdue: due_date_comparisons_1.isDueDateNotOverdue,
        };
        this.systemLockIcon = true;
        this.fieldValueGetter = new due_date_fieldtype_1.DueDateFieldValueGetter();
        this.clipboardPasteRules = {
            daterangefield: clipboard_1.clipboardDateRangeToDueDate,
            duedatefield: clipboard_2.clipboardDueDateToSelf,
            datefield: clipboard_1.clipboardDateToDueDate,
            firstcreatedfield: clipboard_1.clipboardCreatedUpdatedToDueDate,
            lastupdatedfield: clipboard_1.clipboardCreatedUpdatedToDueDate,
            textfield: clipboard_1.clipboardTextToDueDate,
            recordtitlefield: clipboard_1.clipboardTextToDueDate,
            textareafield: clipboard_1.clipboardTextToDueDate,
            unknownfield: clipboard_1.clipboardTextToDueDate,
        };
        this.valueToGoogleSheetArray = (fieldValue, field, userPreferences) => {
            if (this.isValueEmpty(fieldValue)) {
                return [
                    (0, google_sheet_1.makeRowDataValues)("", "stringValue"),
                    (0, google_sheet_1.makeRowDataValues)("", "stringValue"),
                ];
            }
            const formattedValue = (0, date_range_2.getFormattedDateRange)(fieldValue, userPreferences, field.params.display_format, false, true);
            return [
                (0, google_sheet_1.makeRowDataValues)(formattedValue.from_date.date, "stringValue"),
                (0, google_sheet_1.makeRowDataValues)(formattedValue.to_date.date, "stringValue"),
            ];
        };
    }
    get validators() {
        return { required: validation_1.dueDateRequired };
    }
    resolveNestedField(field, slug) {
        const [parentSlug, nestedSlug = "to_date"] = slug.split(".");
        return (0, fields_1.getAppFieldBySlug)([field], `${parentSlug}.${nestedSlug}`);
    }
    sortKey(fieldValue, field, userPreferences, record, structure, rule) {
        const sortByField = this.resolveNestedField(field, rule.field);
        return (0, sort_1.getNestedFieldSortKey)(sortByField, structure, rule, userPreferences, record);
    }
    groupByKey(value, _field, userPreferences) {
        return (0, due_date_1.getDueDateGroupByKey)(value, userPreferences);
    }
    groupBySortKey(value, field, userPreferences) {
        return this.groupByKey(value, field, userPreferences);
    }
    getFilterComponentData(field, comparison) {
        return {
            ...super.getFilterComponentData(field, comparison),
            dateModes: this.dateModes,
            placeholder: (0, intl_1.$t)(texts_1.dueDateFieldText.type_number_of_days),
        };
    }
    getComparisons(field, rule) {
        const [, nestedSlug] = rule.field.split(".");
        if (nestedSlug) {
            const nested = this.resolveNestedField(field, rule.field);
            const nestedFieldLogic = (0, fields_logic_registry_1.getFieldLogic)(nested.field_type);
            return nestedFieldLogic.getComparisons(nested, rule);
        }
        return super.getComparisons(field, rule);
    }
    processFieldValue(record, slug, comparison, field, userPreferences) {
        var _a, _b, _c;
        const [, nestedSlug] = slug.split(".");
        if (nestedSlug) {
            const nested = this.resolveNestedField(field, slug);
            const nestedFieldLogic = (0, fields_logic_registry_1.getFieldLogic)(nested.field_type);
            return nestedFieldLogic.processFieldValue(record, slug, comparison, nested, userPreferences);
        }
        const fieldValue = (0, fields_1.getValueFromRecordByField)(record, field);
        const statusUpdatedOn = (0, date_2.getDateTime)((_a = fieldValue.status_updated_on) !== null && _a !== void 0 ? _a : new Date().toISOString(), userPreferences);
        return {
            ...fieldValue,
            from_date: {
                date: (0, date_2.dateValueToDate)(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.from_date, userPreferences.timezone.value),
                include_time: ((_b = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.from_date) === null || _b === void 0 ? void 0 : _b.include_time) || false,
            },
            to_date: {
                date: (0, date_2.dateValueToDate)(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.to_date, userPreferences.timezone.value),
                include_time: ((_c = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.to_date) === null || _c === void 0 ? void 0 : _c.include_time) || false,
            },
            status_updated_on: statusUpdatedOn,
        };
    }
    processFilterValue(filterValue, comparison, _field, userPreferences) {
        const emptyComparisons = [
            "is_empty",
            "is_not_empty",
            "is_overdue",
            "is_not_overdue",
        ];
        if (emptyComparisons.includes(comparison)) {
            return filterValue;
        }
        const helper = new date_3.BaseDateModeHelper(comparison, filterValue === null || filterValue === void 0 ? void 0 : filterValue.date_mode, filterValue === null || filterValue === void 0 ? void 0 : filterValue.date_mode_value, userPreferences);
        // @ts-expect-error: Poor typing
        const valueGetter = helper[filterValue === null || filterValue === void 0 ? void 0 : filterValue.date_mode];
        if (typeof valueGetter !== "function") {
            return filterValue;
        }
        return valueGetter();
    }
    filterToRecordValue(filterValue, comparison, field, userPreferences) {
        var _a;
        if (comparison !== "is") {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        const emptyValue = this.getEmptyValue();
        const now = (0, date_2.getNow)(userPreferences.timezone.value);
        const today = (0, date_fns_1.startOfDay)(now);
        const isFromDate = field.slug === "from_date";
        if (filterValue.date_mode === "today") {
            const date = (0, date_2.dateToDateValue)(today);
            return {
                ...emptyValue,
                ...(isFromDate ? { from_date: date } : undefined),
                to_date: date,
            };
        }
        if (filterValue.date_mode === "tomorrow") {
            const date = (0, date_2.dateToDateValue)((0, date_fns_1.addDays)(today, 1));
            return {
                ...emptyValue,
                ...(isFromDate ? { from_date: date } : undefined),
                to_date: date,
            };
        }
        if (filterValue.date_mode === "yesterday") {
            const date = (0, date_2.dateToDateValue)((0, date_fns_1.addDays)(today, -1));
            return {
                ...emptyValue,
                ...(isFromDate ? { from_date: date } : undefined),
                to_date: date,
            };
        }
        if ((0, date_3.isDateFilterRangeValue)(filterValue) &&
            ((_a = filterValue.date_mode_value) === null || _a === void 0 ? void 0 : _a.lower)) {
            const isExactDate = filterValue.date_mode_value.lower === filterValue.date_mode_value.upper;
            if (isExactDate) {
                const startDate = (0, date_fns_1.startOfDay)(new Date(filterValue.date_mode_value.lower));
                const date = (0, date_2.dateToDateValue)(startDate);
                return {
                    ...emptyValue,
                    ...(isFromDate ? { from_date: date } : undefined),
                    to_date: date,
                };
            }
        }
        throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
    }
    getGroupLabel(fieldValue, field, userPreferences) {
        return this.groupByKey(fieldValue, field, userPreferences);
    }
    getDateValueFromString(date, includeTime) {
        return {
            from_date: { date, include_time: includeTime },
            to_date: { date, include_time: includeTime },
            status_is_completed: false,
            status_updated_on: null,
        };
    }
    addDuration(fieldValue, duration, userPreferences, includeTime) {
        return (0, date_range_1.addDurationToDateRangeValue)(fieldValue, duration, userPreferences, includeTime);
    }
    valueToString(fieldValue, field, userPreferences, ..._rest) {
        var _a, _b, _c;
        if (!((_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.to_date) === null || _a === void 0 ? void 0 : _a.date)) {
            return "";
        }
        const range = userPreferences
            ? (0, date_range_2.getFormattedDateRange)(fieldValue, userPreferences, field.params.display_format, false)
            : fieldValue;
        if (!((_b = range.from_date) === null || _b === void 0 ? void 0 : _b.date) || !range.to_date.date) {
            return (_c = range.to_date.date) !== null && _c !== void 0 ? _c : "";
        }
        return `${range.from_date.date} → ${range.to_date.date}`;
    }
    valueToGoogleSheet(fieldValue, field, userPreferences) {
        var _a, _b;
        if (this.isValueEmpty(fieldValue)) {
            return (0, google_sheet_1.makeRowDataValues)("", "stringValue");
        }
        const formattedValue = (0, date_range_2.getFormattedDateRange)(fieldValue, userPreferences, field.params.display_format, false, true);
        const value = `${(_a = formattedValue.from_date.date) !== null && _a !== void 0 ? _a : ""} → ${(_b = formattedValue.to_date.date) !== null && _b !== void 0 ? _b : ""}`;
        return (0, google_sheet_1.makeRowDataValues)(value, "stringValue");
    }
    valueToExport(fieldValue, field, userPreferences) {
        var _a, _b, _c, _d;
        if (this.isValueEmpty(fieldValue))
            return "";
        if (!userPreferences)
            return `${(_a = fieldValue.from_date.date) !== null && _a !== void 0 ? _a : ""}|${(_b = fieldValue.to_date.date) !== null && _b !== void 0 ? _b : ""}`;
        const formattedValue = (0, date_range_2.getFormattedDateRange)(fieldValue, userPreferences, field.params.display_format, false, true);
        return `${(_c = formattedValue.from_date.date) !== null && _c !== void 0 ? _c : ""}|${(_d = formattedValue.to_date.date) !== null && _d !== void 0 ? _d : ""}`;
    }
    getNestedExportHeader(label, field) {
        return `${field === null || field === void 0 ? void 0 : field.label}: ${label}`;
    }
    getEmptyValue() {
        return {
            ...(0, date_range_1.emptyDateRange)(),
            status_updated_on: null,
            status_is_completed: false,
        };
    }
    getInitialFieldValue(startValue) {
        return startValue || this.getEmptyValue();
    }
    isValueEmpty(fieldValue) {
        var _a;
        return !((_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.to_date) === null || _a === void 0 ? void 0 : _a.date);
    }
    isValueEqual(fieldValue1, fieldValue2, _field, userPreferences) {
        var _a;
        const isDateRangeEqual = (0, date_range_1.isDateRangesEqual)(fieldValue1, fieldValue2, (_a = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.timezone.value) !== null && _a !== void 0 ? _a : "UTC");
        const isRepeatingTaskEqual = (0, react_fast_compare_1.default)(fieldValue1 === null || fieldValue1 === void 0 ? void 0 : fieldValue1.repeating_task, fieldValue2 === null || fieldValue2 === void 0 ? void 0 : fieldValue2.repeating_task);
        return isDateRangeEqual && isRepeatingTaskEqual;
    }
    getDefaultValue(field, userPreferences) {
        var _a;
        const defaultValue = (0, date_2.parseDefaultValue)(field.params.default_value, (_a = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.timezone.value) !== null && _a !== void 0 ? _a : "UTC");
        if (!defaultValue) {
            // @ts-expect-error: Poor typing
            return;
        }
        return {
            from_date: { date: null, include_time: false },
            to_date: { date: defaultValue, include_time: false },
            status_updated_on: null,
            status_is_completed: false,
        };
    }
    filterValueToString(filterValue, field, userPreferences, comparison) {
        const isOverdueComparison = !!comparison && ["is_overdue", "is_not_overdue"].includes(comparison);
        const isEmptyComparison = !!comparison && ["is_empty", "is_not_empty"].includes(comparison);
        if (isOverdueComparison || isEmptyComparison) {
            return filterValue;
        }
        return !!filterValue && !!comparison
            ? (0, utils_1.dateFieldName)(field, {
                value: filterValue,
                comparison,
                field: field.field_type,
            }, userPreferences)
            : filterValue;
    }
    prepareValueForClipboard(fieldValue, field, userPreferences) {
        return {
            text: (0, utils_2.dateRangeToClipboardText)(fieldValue, userPreferences, field.params.display_format),
            rawValue: fieldValue,
        };
    }
    getGridColumnSettings(field) {
        return {
            ...super.getGridColumnSettings(field),
            contentAlignment: "centered",
        };
    }
    canChangeValue(field, fromValue, toValue) {
        return this.isValueEqual(fromValue, toValue, field);
    }
    searchKey(fieldValue, field, userPreferences) {
        return (0, date_range_1.getDateRangeSearchKey)(field, fieldValue, userPreferences);
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 170,
            minWidth: 90,
        };
    }
    getRecordTitleVariableValue(variableName, fieldValue, field, userPreferences) {
        var _a, _b, _c;
        if (!((_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.to_date) === null || _a === void 0 ? void 0 : _a.date)) {
            return "";
        }
        const range = userPreferences
            ? (0, date_range_2.getFormattedDateRange)(fieldValue, userPreferences, field.params.display_format, false, true)
            : fieldValue;
        if (!((_b = range.from_date) === null || _b === void 0 ? void 0 : _b.date) || !range.to_date.date) {
            return (_c = range.to_date.date) !== null && _c !== void 0 ? _c : "";
        }
        return `${range.from_date.date} → ${range.to_date.date}`;
    }
    migrateField(fieldTo, fieldFrom) {
        const migratedField = super.migrateField(fieldTo, fieldFrom);
        return (0, migration_1.migrateDisplayFormat)(migratedField, types_1.dueDateDisplayFormats, String(fieldTo.params.display_format));
    }
    getEmptyFilterValue(_field, comparison) {
        var _a;
        const comparisonsWithNullValue = [
            "is_empty",
            "is_not_empty",
            "is_overdue",
            "is_not_overdue",
        ].includes(comparison);
        if (comparisonsWithNullValue) {
            return null;
        }
        const dateMode = (_a = this.dateModes[comparison]) !== null && _a !== void 0 ? _a : this.dateModes.default;
        if (!dateMode) {
            return null;
        }
        const defaultDateComparison = dateMode[0];
        return (0, date_3.getDateEmptyFilterValue)(defaultDateComparison.value, defaultDateComparison.label);
    }
    copyValue(value) {
        if (!value) {
            return value;
        }
        return {
            from_date: { ...value.from_date },
            to_date: { ...value.to_date },
        };
    }
    getContentSize(fieldValue, field, userPreferences, record, font) {
        var _a;
        if (!((_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.to_date) === null || _a === void 0 ? void 0 : _a.date)) {
            return 0;
        }
        const pillIcon = 25;
        const dateString = (0, date_2.formatDateValueToDisplayFormat)(fieldValue.to_date, userPreferences, field.params.display_format);
        const size = (0, dom_1.getTextSize)(dateString, undefined, font);
        return size + pillIcon;
    }
    getViewControlRules(field) {
        return {
            ...super.getViewControlRules(field),
            nestedFieldsInGroup: "hide",
        };
    }
    getInlineEditRules(field) {
        return {
            ...super.getInlineEditRules(field),
            saveKeys: [],
            closeWhenClickOutside: false,
            editorWidth: "100%",
            allowAddRecordToGroup: false,
            allowEditNestedFields: true,
        };
    }
    shouldCleanUpEmptyInLookupFilter(comparison) {
        return !["is_overdue", "is_not_overdue"].includes(comparison);
    }
}
exports.DueDateFieldLogic = DueDateFieldLogic;
